import SelectComponent from "@via/components/select/select";
import { SkeletonLoader } from "@via/components/loading/loading";
import { BUILT_IN_ACCESS_OPTION_ID } from "@via/pages/data/constants";
import { SampleTrackerViewType } from "@via/pages/sample-trackers/sample-trackers-filter";
import {
  SAMPLESHEET_FILE_LOCATION_OPTIONS,
  SOURCE_PATH_DERIVATION_OPTIONS,
  type SchedulerFormValues,
} from "@via/pages/runs/schedulers/utils/helpers";
import type { SelectOption } from "@via/utils/interfaces";
import { classNames } from "@via/utils/helpers";
import type { JSX } from "react";
import {
  type Control,
  type UseFormRegister,
  type UseFormSetValue,
} from "react-hook-form";
import type {
  RunSchedulerOverwriteMethod,
  RunSchedulerOverwriteParameter,
  RunSchedulerOverwriteValueType,
  RunSchedulerSourceMethod,
} from "common";

interface OverwriteRowCardProps {
  idx: number;
  fieldId: string;
  row: RunSchedulerOverwriteParameter;
  sourceMethod: RunSchedulerSourceMethod;
  selectedCollectionId: string;
  control: Control<SchedulerFormValues>;
  register: UseFormRegister<SchedulerFormValues>;
  setValue: UseFormSetValue<SchedulerFormValues>;
  overwriteFieldOptions: SelectOption[];
  overwriteValueTypeOptions: Array<{
    label: string;
    value: RunSchedulerOverwriteValueType;
  }>;
  overwriteMethodOptions: Array<{
    label: string;
    value: RunSchedulerOverwriteMethod;
  }>;
  amazonCredentialOptions: SelectOption[];
  googleCredentialOptions: SelectOption[];
  vmetaCanvasOptions: SelectOption[];
  isOverwriteFieldsLoading: boolean;
  isVmetaCanvasLoading: boolean;
  datasetTargetView: SampleTrackerViewType;
  setDatasetTargetView: (v: SampleTrackerViewType) => void;
  onRemove: () => void;
}

export function OverwriteRowCard({
  idx,
  fieldId,
  row,
  sourceMethod,
  selectedCollectionId,
  control,
  register,
  setValue,
  overwriteFieldOptions,
  overwriteValueTypeOptions,
  overwriteMethodOptions,
  amazonCredentialOptions,
  googleCredentialOptions,
  vmetaCanvasOptions,
  isOverwriteFieldsLoading,
  isVmetaCanvasLoading,
  datasetTargetView,
  setDatasetTargetView,
  onRemove,
}: OverwriteRowCardProps): JSX.Element {
  const isSampleTrackerFieldDisabled =
    sourceMethod !== "sample-tracker" || !selectedCollectionId;
  const isSampleTrackerOverwrite = sourceMethod === "sample-tracker";
  const isSaveAsDataset = (row.method ?? "replace") === "save-as-dataset";
  const showDatasetTarget = isSaveAsDataset;
  const showDatasetNameField = isSaveAsDataset && isSampleTrackerOverwrite;
  const showFileLocation = isSaveAsDataset && isSampleTrackerOverwrite;
  const samplesheetLocation = row.samplesheetFileLocation ?? "run-environment";

  return (
    <div
      key={fieldId}
      className="rounded-md border border-gray-100 bg-gray-50 p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
        <div
          className={classNames(
            isSampleTrackerOverwrite ? "md:col-span-4" : "md:col-span-5"
          )}>
          <label className="block text-sm font-medium text-gray-900">
            Config parameter
          </label>
          <div className="mt-2">
            <input
              {...register(`overwriteRows.${idx}.configParameter`)}
              placeholder="e.g. inputs.reads"
              className="w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
            />
          </div>
        </div>

        {isSampleTrackerOverwrite && (
          <div className="md:col-span-4">
            <label className="block text-sm font-medium text-gray-900">
              Value from tracker field
            </label>
            <div className="mt-2">
              {isOverwriteFieldsLoading && selectedCollectionId ? (
                <SkeletonLoader count={1} />
              ) : (
                <SelectComponent
                  control={control}
                  name={`overwriteRows.${idx}.sampleTrackerField`}
                  options={overwriteFieldOptions}
                  placeholder={
                    isSampleTrackerFieldDisabled
                      ? "Select a Sample Tracker collection first"
                      : "Select a field"
                  }
                  readOnly={isSampleTrackerFieldDisabled}
                />
              )}
            </div>
            {isSampleTrackerFieldDisabled && (
              <p className="mt-1 text-xs text-gray-500">
                This option requires Source method = Sample Tracker and a
                selected Collection.
              </p>
            )}
          </div>
        )}

        {sourceMethod === "watch-path" && (
          <div className="md:col-span-3">
            <label className="block text-sm font-medium text-gray-900">
              New value
            </label>
            <div className="mt-2">
              <SelectComponent
                control={control}
                name={`overwriteRows.${idx}.valueType`}
                options={overwriteValueTypeOptions}
                enableClear={false}
                isSearchable={false}
                onChange={selected => {
                  const nextType = (selected as SelectOption)
                    .value as RunSchedulerOverwriteValueType;
                  setValue(`overwriteRows.${idx}.textValue`, "");
                  setValue(`overwriteRows.${idx}.sampleTrackerField`, "");
                  setValue(`overwriteRows.${idx}.relativePath`, "");
                  setValue(`overwriteRows.${idx}.jsonFileRelativePath`, "");
                  setValue(`overwriteRows.${idx}.jsonKeyDotPath`, "");
                  setValue(
                    `overwriteRows.${idx}.sourcePathDerivation`,
                    nextType === "derive-from-source-path" ? "full" : undefined
                  );
                }}
              />
            </div>
          </div>
        )}

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-900">
            Method
          </label>
          <div className="mt-2">
            <SelectComponent
              control={control}
              name={`overwriteRows.${idx}.method`}
              options={overwriteMethodOptions}
              enableClear={false}
              isSearchable={false}
            />
          </div>
        </div>

        <div className="md:col-span-2 flex items-end justify-end">
          <button
            type="button"
            onClick={onRemove}
            className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-red-600 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
            Remove
          </button>
        </div>
      </div>

      {showDatasetTarget && (
        <DatasetTargetFields
          idx={idx}
          row={row}
          selectedCollectionId={selectedCollectionId}
          control={control}
          setValue={setValue}
          overwriteFieldOptions={overwriteFieldOptions}
          amazonCredentialOptions={amazonCredentialOptions}
          googleCredentialOptions={googleCredentialOptions}
          vmetaCanvasOptions={vmetaCanvasOptions}
          isOverwriteFieldsLoading={isOverwriteFieldsLoading}
          isVmetaCanvasLoading={isVmetaCanvasLoading}
          datasetTargetView={datasetTargetView}
          setDatasetTargetView={setDatasetTargetView}
          showFileLocation={showFileLocation}
          showDatasetNameField={showDatasetNameField}
          samplesheetLocation={samplesheetLocation}
          isSampleTrackerFieldDisabled={isSampleTrackerFieldDisabled}
        />
      )}

      {isSampleTrackerOverwrite ? null : (
        <WatchPathValueFields
          idx={idx}
          row={row}
          sourceMethod={sourceMethod}
          selectedCollectionId={selectedCollectionId}
          control={control}
          register={register}
          overwriteFieldOptions={overwriteFieldOptions}
          isOverwriteFieldsLoading={isOverwriteFieldsLoading}
          isSampleTrackerFieldDisabled={isSampleTrackerFieldDisabled}
        />
      )}
    </div>
  );
}

function DatasetTargetFields({
  idx,
  row,
  selectedCollectionId,
  control,
  setValue,
  overwriteFieldOptions,
  amazonCredentialOptions,
  googleCredentialOptions,
  vmetaCanvasOptions,
  isOverwriteFieldsLoading,
  isVmetaCanvasLoading,
  datasetTargetView,
  setDatasetTargetView,
  showFileLocation,
  showDatasetNameField,
  samplesheetLocation,
  isSampleTrackerFieldDisabled,
}: {
  idx: number;
  row: RunSchedulerOverwriteParameter;
  selectedCollectionId: string;
  control: Control<SchedulerFormValues>;
  setValue: UseFormSetValue<SchedulerFormValues>;
  overwriteFieldOptions: SelectOption[];
  amazonCredentialOptions: SelectOption[];
  googleCredentialOptions: SelectOption[];
  vmetaCanvasOptions: SelectOption[];
  isOverwriteFieldsLoading: boolean;
  isVmetaCanvasLoading: boolean;
  datasetTargetView: SampleTrackerViewType;
  setDatasetTargetView: (v: SampleTrackerViewType) => void;
  showFileLocation: boolean;
  showDatasetNameField: boolean;
  samplesheetLocation: string;
  isSampleTrackerFieldDisabled: boolean;
}): JSX.Element {
  return (
    <div className="mt-4 rounded-md border border-gray-200 bg-white p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-12">
        {showFileLocation && (
          <div className="md:col-span-4">
            <label className="block text-sm font-medium text-gray-900">
              File location
            </label>
            <div className="mt-2">
              <SelectComponent
                control={control}
                name={`overwriteRows.${idx}.samplesheetFileLocation`}
                options={SAMPLESHEET_FILE_LOCATION_OPTIONS}
                enableClear={false}
                isSearchable={false}
                onChange={selected => {
                  const next = (selected as SelectOption).value as
                    | "run-environment"
                    | "s3"
                    | "gcs";
                  setValue(
                    `overwriteRows.${idx}.samplesheetAmazonCreId`,
                    next === "s3"
                      ? (row.samplesheetAmazonCreId ??
                          BUILT_IN_ACCESS_OPTION_ID)
                      : undefined
                  );
                  setValue(
                    `overwriteRows.${idx}.samplesheetGoogleCreId`,
                    next === "gcs"
                      ? (row.samplesheetGoogleCreId ??
                          BUILT_IN_ACCESS_OPTION_ID)
                      : undefined
                  );
                }}
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              The path value will be read from the selected tracker field.
            </p>
          </div>
        )}

        {showFileLocation && samplesheetLocation === "s3" && (
          <div className="md:col-span-4">
            <label className="block text-sm font-medium text-gray-900">
              AWS credential
            </label>
            <div className="mt-2">
              <SelectComponent
                control={control}
                name={`overwriteRows.${idx}.samplesheetAmazonCreId`}
                options={amazonCredentialOptions}
                enableClear={false}
              />
            </div>
          </div>
        )}

        {showFileLocation && samplesheetLocation === "gcs" && (
          <div className="md:col-span-4">
            <label className="block text-sm font-medium text-gray-900">
              GCP credential
            </label>
            <div className="mt-2">
              <SelectComponent
                control={control}
                name={`overwriteRows.${idx}.samplesheetGoogleCreId`}
                options={googleCredentialOptions}
                enableClear={false}
              />
            </div>
          </div>
        )}

        <div className="md:col-span-4">
          <label className="block text-sm font-medium text-gray-900">
            Save dataset to sample tracker
          </label>
          <div className="mt-2">
            <span className="isolate inline-flex w-full rounded-md">
              <button
                type="button"
                onClick={() => setDatasetTargetView(SampleTrackerViewType.My)}
                className={classNames(
                  "relative inline-flex flex-1 select-none items-center justify-center rounded-l-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10",
                  datasetTargetView === SampleTrackerViewType.My
                    ? "bg-indigo-600 text-white hover:bg-indigo-500"
                    : "bg-white text-gray-900"
                )}>
                My Trackers
              </button>
              <button
                type="button"
                onClick={() =>
                  setDatasetTargetView(SampleTrackerViewType.Shared)
                }
                className={classNames(
                  "relative -ml-px inline-flex flex-1 select-none items-center justify-center rounded-r-md px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10",
                  datasetTargetView === SampleTrackerViewType.Shared
                    ? "bg-indigo-600 text-white hover:bg-indigo-500"
                    : "bg-white text-gray-900"
                )}>
                Shared with Me
              </button>
            </span>
          </div>
          <div className="mt-2">
            {isVmetaCanvasLoading ? (
              <SkeletonLoader count={1} />
            ) : (
              <SelectComponent
                control={control}
                name={`overwriteRows.${idx}.samplesheetTargetCanvasId`}
                options={vmetaCanvasOptions}
                placeholder="Select a sample tracker"
                readOnly={vmetaCanvasOptions.length === 0}
              />
            )}
          </div>
        </div>

        {showDatasetNameField ? (
          <div className="md:col-span-4">
            <label className="block text-sm font-medium text-gray-900">
              Dataset name field (optional, from source tracker row)
            </label>
            <div className="mt-2">
              {isOverwriteFieldsLoading && selectedCollectionId ? (
                <SkeletonLoader count={1} />
              ) : (
                <SelectComponent
                  control={control}
                  name={`overwriteRows.${idx}.samplesheetDatasetNameField`}
                  options={overwriteFieldOptions}
                  placeholder="Select a field"
                  readOnly={isSampleTrackerFieldDisabled}
                />
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              If omitted, the dataset name will be derived from the detected
              source value.
            </p>
          </div>
        ) : (
          <div className="md:col-span-8">
            <p className="mt-8 text-xs text-gray-500">
              Dataset name will be derived automatically from the detected
              source folder name.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function WatchPathValueFields({
  idx,
  row,
  sourceMethod,
  selectedCollectionId,
  control,
  register,
  overwriteFieldOptions,
  isOverwriteFieldsLoading,
  isSampleTrackerFieldDisabled,
}: {
  idx: number;
  row: RunSchedulerOverwriteParameter;
  sourceMethod: RunSchedulerSourceMethod;
  selectedCollectionId: string;
  control: Control<SchedulerFormValues>;
  register: UseFormRegister<SchedulerFormValues>;
  overwriteFieldOptions: SelectOption[];
  isOverwriteFieldsLoading: boolean;
  isSampleTrackerFieldDisabled: boolean;
}): JSX.Element {
  return (
    <div className="mt-4">
      {sourceMethod !== "watch-path" &&
      row.valueType === "sample-tracker-field" ? (
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Value from tracker field
          </label>
          <div className="mt-2">
            {isOverwriteFieldsLoading && selectedCollectionId ? (
              <SkeletonLoader count={1} />
            ) : (
              <SelectComponent
                control={control}
                name={`overwriteRows.${idx}.sampleTrackerField`}
                options={overwriteFieldOptions}
                placeholder={
                  isSampleTrackerFieldDisabled
                    ? "Select a Sample Tracker collection first"
                    : "Select a field"
                }
                readOnly={isSampleTrackerFieldDisabled}
              />
            )}
          </div>
          {isSampleTrackerFieldDisabled && (
            <p className="mt-1 text-xs text-gray-500">
              This option requires Source method = Sample Tracker and a selected
              Collection.
            </p>
          )}
        </div>
      ) : row.valueType === "relative-path" ? (
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Relative path (under detected new folder)
          </label>
          <div className="mt-2">
            <input
              {...register(`overwriteRows.${idx}.relativePath`)}
              placeholder="e.g. a/samplesheet.csv"
              className="w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
            />
          </div>
        </div>
      ) : row.valueType === "derive-from-source-path" ? (
        <div>
          <label className="block text-sm font-medium text-gray-900">
            Derivation
          </label>
          <div className="mt-2">
            <SelectComponent
              control={control}
              name={`overwriteRows.${idx}.sourcePathDerivation`}
              options={SOURCE_PATH_DERIVATION_OPTIONS}
              enableClear={false}
              isSearchable={false}
            />
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Uses the detected source path at execution time.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div>
            <label className="block text-sm font-medium text-gray-900">
              JSON file relative path
            </label>
            <div className="mt-2">
              <input
                {...register(`overwriteRows.${idx}.jsonFileRelativePath`)}
                placeholder="e.g. b/manifest.json"
                className="w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-900">
              JSON key (dot notation)
            </label>
            <div className="mt-2">
              <input
                {...register(`overwriteRows.${idx}.jsonKeyDotPath`)}
                placeholder="e.g. runInfo.sampleId"
                className="w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Use dots to navigate JSON keys (e.g.{" "}
              <span className="font-mono">a.b.c</span>).
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
